import { ExportIcon, InformationIcon } from 'components/icons';
import { Skeleton } from 'components/ui';
import { format, isValid, parse } from 'date-fns';
import { orderBy } from 'lodash';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useGetProductsCohortsQuery,
  useGetCustomerSegmentsQuery,
  useGetProductImageQuery,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';
import { mapOrder, opacityScaling } from 'utils/helpers';
import { useQuery } from 'utils/useQuery';
import { CohortTimeAnchorSelect } from '../../analytics/Cohort/CohortTimeAnchorSelect';
import { RetentionHeatmapLoadingState } from '../../analytics';
import { ProductImage } from '../ProductImage';
import { formatNumber } from '../../analytics/ExecutiveSummary/helpers';
import { getProductCohortDetails } from './productCohortDetails';
import { ProductCohortSummary } from './ProductCohortSummary';
import { useSortBy, useTable } from 'react-table';
import {
  SortAscendingIcon,
  SortDescendingIcon,
} from '@heroicons/react/outline';
import { Tooltip } from '../../ui';

const ExportMenuLazy = lazy(
  () => import('components/analytics/Cohort/CohortExportMenu')
);
type CohortType = 'revenue_growth' | 'repeat_purchase' | 'churn_rate';

export default function ProductCohortBase({
  cohort_type,
}: {
  cohort_type: CohortType;
}) {
  const cohortDetails = getProductCohortDetails(cohort_type);

  const query = useQuery();
  const store_name = query.get('store_name') || '';
  const start_date = query.get('start_date') || '';
  const end_date = query.get('end_date') || '';
  const cumulative = false;
  const lapseRangeType = query.get('lapse_range_type') || 'calendar';

  const params = {
    store_name,
    start_date,
    end_date,
    cohort_event_type: 'first_order_product',
    ticking: 'month',
    time_anchor: lapseRangeType,
    lapse_metric: cohortDetails?.presets.lapse_metric || '',
    overall_metrics: cohortDetails?.presets.overall_metrics.toString() || '',
    append_metrics: 'aclv',
    cumulative: cumulative.toString(),
  };

  const {
    output: { filtersApiPayload },
    applied: { segment: appliedSegment },
  } = useAppSelector(composableFiltersState);

  const { isFetching: isFetchingSegments } = useGetCustomerSegmentsQuery(
    { serializedStoreName: `store_name=${store_name}` },
    {
      skip: !store_name,
    }
  );

  const urlSegmentName = query.get('segment_name');

  const { data: cohortData, isFetching } = useGetProductsCohortsQuery(
    {
      serializedParams: new URLSearchParams(params).toString(),
      body: filtersApiPayload,
    },
    {
      skip:
        !store_name ||
        !start_date ||
        !end_date ||
        Boolean(urlSegmentName && !appliedSegment),
    }
  );

  // ======================starts prefetching==========================

  useGetProductsCohortsQuery(
    {
      serializedParams: new URLSearchParams({
        ...params,
        time_anchor: lapseRangeType === 'calendar' ? 'relative' : 'calendar',
      }).toString(),
      body: filtersApiPayload,
    },
    {
      skip:
        !store_name ||
        !start_date ||
        !end_date ||
        Boolean(urlSegmentName && !appliedSegment),
    }
  );

  // ======================finished prefetching==========================

  const isLoading = isFetching || isFetchingSegments;

  const { currentStore } = useAppSelector(userDetailsState);
  const locale = currentStore.completeInfo?.primary_locale ?? 'en';
  const currency = currentStore.completeInfo?.currency ?? 'USD';

  const warningMessage = cohortData?.warning;

  useEffect(() => {
    if (warningMessage) {
      toast.info(warningMessage, { toastId: 'cohort-warning' });
    }
  }, [warningMessage]);

  const data = useMemo(() => {
    let lapseMetricValues: number[] = [];
    let lapseMetricColumns = new Set<string>([]);

    let lapseMetricsTooltips = new Map();

    const sortByColumn = cohortData?.data.sort_by_column ?? 'cohort_name';
    const sortingOrder: 'asc' | 'desc' = 'desc';

    const firstCohort = cohortData?.data.cohorts[0];

    let data: Record<string, any>[] = [];

    if (cohortData) {
      cohortData.data.cohorts.forEach(({ lapse_metric, metrics, ...rest }) => {
        const overallMetrics = metrics.reduce(
          (acc, metric) => ({
            ...acc,
            [metric.column_name]: metric.value,
          }),
          {}
        );

        const lapseMetricsData = lapse_metric?.data
          .filter((metric) => metric.value)
          .reduce(
            (acc, metric) => ({
              ...acc,
              [metric.name]: metric.value,
            }),
            {}
          );

        data.push({ ...rest, ...overallMetrics, ...lapseMetricsData });

        lapse_metric?.data.forEach((metric) => {
          if (metric.value) {
            lapseMetricValues.push(metric.value);

            lapseMetricsTooltips.set(
              metric.name + metric.value,
              metric.tooltip
            );

            lapseMetricColumns.has(metric.name) ||
              lapseMetricColumns.add(metric.name);
          }
        });
      });
    }

    return {
      lapseMetricsTooltips,
      tableData: sortByColumn
        ? orderBy(data, [sortByColumn], [sortingOrder])
        : data,
      lapseMetricColumns: {
        name: firstCohort?.lapse_metric?.name,
        label: firstCohort?.lapse_metric?.label,
        format: firstCohort?.lapse_metric?.format || 'string',
        data: Array.from(lapseMetricColumns).sort((a: any, b: any) => {
          if (a[0] === 'M') {
            return a.slice(1) - b.slice(1);
          }
          const parsedA = isValid(parse(a, 'yyyy-MM', new Date()))
            ? parse(a, 'yyyy-MM', new Date())
            : null;
          const parsedB = isValid(parse(b, 'yyyy-MM', new Date()))
            ? parse(b, 'yyyy-MM', new Date())
            : null;
          if (parsedA && parsedB) {
            return parsedA.getTime() - parsedB.getTime();
          }
          return a - b;
        }),
        maxValue: Math.max(...lapseMetricValues) || 1,
        minValue: Math.min(...lapseMetricValues) || 1,
      },
      columns:
        firstCohort?.metrics.map((metric) => ({
          name: metric.column_name,
          label: metric.column_label,
          format: metric.format,
        })) || [],
      sortByColumn,
      sortingOrder,
    };
  }, [cohortData]);

  const columns = useMemo(() => {
    const colorShadingRGB: Record<CohortType, string> = {
      churn_rate: '213,203,183',
      repeat_purchase: '125,169,179',
      revenue_growth: '125,169,179',
    };
    const columns = [
      {
        Header: 'Cohort',
        sortType: 'basic',
        className: 'py-2',
        accessor: 'cohort_name',
        Cell: ProductCohortNameColumn,
      },
    ];

    if (data.columns.length > 0) {
      const columnsWithoutWarningAndName = data.columns.filter((c) => {
        const removeColumns = ['warning', 'cohort_name'];
        return !removeColumns.includes(c.name);
      });

      const sortedColumns = mapOrder(
        columnsWithoutWarningAndName,
        cohortDetails?.presets.overall_metrics || [],
        'name'
      );

      sortedColumns.forEach((column) => {
        columns.push({
          Header: column.label,
          sortType: 'basic',
          accessor: column.name,
          className: '',
          Cell: ({ value }: { value: any; row: any }) => (
            <p className="px-4 text-center">
              {formatNumber(value, {
                type: column.format as 'number' | 'percent' | 'currency',
                locale,
                currency,
              })}
            </p>
          ),
        });
      });
    }

    if (data.lapseMetricColumns.data.length > 0) {
      const column: any = {
        Header: () => (
          <p className="uppercase ">{`${data.lapseMetricColumns.label} `}</p>
        ),
        id: 'lapse_metric',
        disableSortBy: true,
        className: 'border-b border-l border-black text-left px-3 py-2',
        columns: [],
      };

      data.lapseMetricColumns.data.forEach((metric, index) => {
        column.columns.push({
          Header: metric,
          disableSortBy: true,
          accessor: metric,
          className: `font-normal text-center py-1 px-1 truncate  ${
            index === 0 && 'border-l border-black'
          }`,
          Cell: ({ value }: { value: any; row: any }) => {
            return value ? (
              <div
                className="px-4 hover:font-bold text-center py-5 cursor-default"
                style={{
                  background: `rgba(${
                    colorShadingRGB[cohort_type]
                  }, ${opacityScaling(value, {
                    min: data.lapseMetricColumns.minValue,
                    max: data.lapseMetricColumns.maxValue,
                  })})`,
                }}
              >
                <Tooltip text={data.lapseMetricsTooltips.get(metric + value)}>
                  <p className={``}>
                    {formatNumber(value, {
                      type: data.lapseMetricColumns.format as
                        | 'number'
                        | 'percent'
                        | 'currency',
                      locale,
                      currency,
                    })}
                  </p>
                </Tooltip>
              </div>
            ) : (
              <p className="py-2 px-4 invisible">0 </p>
            );
          },
        });
      });

      columns.push(column);
    }

    return columns;
  }, [
    cohortDetails?.presets.overall_metrics,
    cohort_type,
    currency,
    data.columns,
    data.lapseMetricColumns.data,
    data.lapseMetricColumns.format,
    data.lapseMetricColumns.label,
    data.lapseMetricColumns.maxValue,
    data.lapseMetricColumns.minValue,
    data.lapseMetricsTooltips,
    locale,
  ]);

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-cohort-repeat_purchase-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  const productMetaList = useAppSelector((state) => {
    return state.productMeta;
  });

  function generateCSVData() {
    const csvHeaders = [
      ...data.columns.map((column) => ({
        label: column.label,
        key: column.name,
        format: column.format,
      })),
      ...data.lapseMetricColumns.data.map((metric) => ({
        label: metric,
        key: metric,
        format: data.lapseMetricColumns.format,
      })),
    ];

    const csvData = data.tableData.map((row) => {
      let csvRow: Record<string, any> = {};

      csvHeaders.forEach((header) => {
        csvRow[header.key] =
          row[header.key] &&
          formatNumber(row[header.key], {
            locale,
            currency,
            type: header.format as 'number' | 'percent' | 'currency',
          });
      });

      try {
        csvRow['cohort_name'] =
          isValid(row.cohort_name) &&
          format(parse(row.cohort_name, 'yyyy-MM-dd', new Date()), 'MMM yyyy');
      } catch (e) {
        csvRow['cohort_name'] = productMetaList.find(
          (item) => item.id === row.cohort_name
        )?.title;
      }

      return csvRow;
    });

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: data.tableData,
        initialState: {
          // @ts-ignore
          sortBy: [{ id: data.sortByColumn, desc: data.sortingOrder }],
        },
      },
      useSortBy
    );

  return (
    <div className="mt-2">
      <div className="items-center justify-between md:flex">
        <div className="max-w-2xl">
          {/* <h3 className="text-xl">{cohortDetails?.title}</h3> */}
          <p className="mt-2 text-black text-opacity-50">
            {cohortDetails?.description}
          </p>
        </div>

        <Skeleton isLoaded={!isLoading}>
          <Suspense
            fallback={
              <div className="p-3 rounded-full bg-segments-gray focus:outline-none flex items-center space-x-2">
                <ExportIcon className="w-5 h-5" />
                <p>Export</p>
              </div>
            }
          >
            <ExportMenuLazy
              generateCSVData={generateCSVData}
              filename={filename}
            />
          </Suspense>
        </Skeleton>
      </div>

      <div>
        <Skeleton isLoaded={!isLoading}>
          <ProductCohortSummary
            data={data}
            cohort_type={cohort_type}
            lapseRangeType={lapseRangeType as 'calendar' | 'relative'}
          />
        </Skeleton>
        <div className={`flex items-center justify-between mt-5`}>
          <div>
            <p className={'italic text-right text-segments-13px'}>
              <span className="inline-block align-text-bottom mr-1">
                <InformationIcon className="w-4 h-4" />
              </span>
              Top 30 product cohorts displayed
            </p>
          </div>
          <div className="flex space-x-8 items-center">
            <CohortTimeAnchorSelect />
          </div>
        </div>
        {isLoading ? (
          <RetentionHeatmapLoadingState />
        ) : (
          <>
            <div className="overflow-x-auto map-container">
              <table
                {...getTableProps()}
                className="w-full mt-1 table-auto text-segments-13px"
              >
                <thead className="border-b border-black">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps([
                            {
                              className:
                                // @ts-ignore
                                column.className +
                                ' first:bg-segments-lighter-gray first:sticky first:left-0',
                            },
                            // @ts-ignore
                            column.getSortByToggleProps(),
                          ])}
                        >
                          {/*@ts-ignore*/}
                          {column.isSorted ? (
                            <div className={`flex items-end justify-center `}>
                              {column.render('Header')}
                              {/*@ts-ignore*/}
                              {column.isSortedDesc ? (
                                <SortDescendingIcon className="w-4 h-4 ml-1" />
                              ) : (
                                <SortAscendingIcon className="w-4 h-4 ml-1" />
                              )}
                            </div>
                          ) : (
                            <>{column.render('Header')}</>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="text-base">
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className="border-b hover:bg-segments-accent-green-3"
                      >
                        {row.cells.map((cell) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className="p-0 first:sticky first:left-0 first:bg-segments-lighter-gray"
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProductCohortNameColumn({ value }: { value: string; row: any }) {
  const productId = Number(value);

  const { serializedStoreName } = useParseURLQueryParams();

  const { data, isFetching } = useGetProductImageQuery(
    {
      serializedStoreName: String(serializedStoreName),
      product_id: String(productId),
    },
    {
      skip: !serializedStoreName || !productId,
    }
  );

  const productTitle = data ? (data.length > 0 ? data[0].title || '' : '') : '';

  return (
    <div className="flex w-60 space-x-3 items-center">
      <Skeleton isLoaded={!isFetching} className="w-10 h-10 rounded-md">
        <ProductImage
          productId={productId}
          loading="lazy"
          height="40"
          width="40"
          className="h-10 w-10 rounded-md"
        />
      </Skeleton>
      <Skeleton className="w-40 h-10" isLoaded={!isFetching}>
        <p className="font-bold text-segments-13px line-clamp-3">
          {productTitle}
        </p>
      </Skeleton>
    </div>
  );
}
